import { BRAND, type Account, type InviteInfo, t } from '@procumi/shared';
import { useState } from 'react';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';
import { rich } from '../lib/rich';

/**
 * Ecranul pe care îl vede colegul invitat, înainte să aibă cont.
 *
 * Emailul nu se poate schimba: e cel pe care s-a făcut invitația, altfel
 * linkul ar fi o ușă deschisă către orice adresă. Rămân de completat numele și
 * parola.
 */
export function InviteScreen({
  token,
  onDone,
  onGiveUp,
}: {
  token: string;
  onDone: (account: Account) => void;
  /** „Nu sunt eu" sau invitație expirată: înapoi la ecranul obișnuit. */
  onGiveUp: () => void;
}) {
  const { data, error, loading } = useAsync<InviteInfo>(() => api.inviteInfo(token), [token]);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setProblema(null);
    try {
      onDone(await api.acceptInvite({ token, name, password }));
    } catch (err) {
      setProblema(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <img className="logo" src="/brand/procumi-lockup.svg" alt={BRAND.name} />
          <small>{BRAND.tagline}</small>
        </div>

        <div className="sheet pad" style={{ display: 'grid', gap: 14 }}>
          {loading ? <Loading /> : null}

          {!loading && error ? (
            <>
              <ErrorBox error={error} />
              <button className="btn" type="button" onClick={onGiveUp}>
                {t('Mergi la ecranul de intrare')}
              </button>
            </>
          ) : null}

          {!loading && data ? (
            <>
              <div>
                <h2>{t('Ai fost invitat în {0}', data.companyName)}</h2>
                <p className="hint" style={{ marginTop: 6 }}>
                  {rich('Contul se face pe <b>{0}</b>. Alege-ți o parolă și intri direct.', data.email)}
                </p>
              </div>

              <label className="f" htmlFor="inv-name">
                {t('Numele tău')}
                <input
                  id="inv-name"
                  className="inp"
                  value={name}
                  placeholder={t('ex. Alex Ionescu')}
                  autoComplete="name"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="f" htmlFor="inv-password">
                {t('Parolă')}
                <input
                  id="inv-password"
                  className="inp"
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="hint" style={{ margin: 0 }}>
                  {t('Cel puțin 8 caractere.')}
                </span>
              </label>

              {problema ? (
                <div className="auth-err" role="alert">
                  {problema}
                </div>
              ) : null}

              <button className="btn primary" type="submit" disabled={busy}>
                {busy ? t('Se face contul…') : t('Intră în firmă')}
              </button>
              <button className="btn" type="button" onClick={onGiveUp}>
                {t('Nu sunt eu')}
              </button>
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
}
